import { useEffect, useState } from "react";
import { addDoc, collection, getDocs, limit, onSnapshot, query, where } from "firebase/firestore";
import { lotKey, lotName, paths, type Lot } from "@clevis/shared-types";
import { db } from "./firebase";

// Lots: Customer → Project → Lot → Jobs. Optional — homeowner and commercial
// work has none. Booking a job for "Lot 26" finds that lot in the project (or
// makes it), so the install, punch and warranty visits all sit under one lot.

export type LotRow = Lot & { id: string };

/** A project's lots, sorted the way people count them (Lot 2 before Lot 10). Office only. */
export function useProjectLots(tenantId: string | null | undefined, projectId: string | null | undefined, enabled = true): LotRow[] {
  const [lots, setLots] = useState<LotRow[]>([]);
  useEffect(() => {
    setLots([]);
    if (!tenantId || !projectId || !enabled) return;
    return onSnapshot(
      collection(db, paths.tenantProjectLots(tenantId, projectId)),
      (snap) =>
        setLots(
          snap.docs
            .map((d) => ({ id: d.id, ...(d.data() as Lot) }))
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })),
        ),
      () => setLots([]),
    );
  }, [tenantId, projectId, enabled]);
  return lots;
}

/** The project's lot for what someone typed: one already loaded, one already saved, or a new one. */
export async function resolveLot(
  tenantId: string,
  projectId: string,
  label: string,
  known: LotRow[],
  extra: { address?: string; projectItemId?: string | null; projectItemName?: string } = {},
): Promise<{ id: string; name: string } | null> {
  const key = lotKey(label);
  if (!key) return null;
  const hit = known.find((l) => l.key === key);
  if (hit) return { id: hit.id, name: hit.name };
  const col = collection(db, paths.tenantProjectLots(tenantId, projectId));
  const saved = await getDocs(query(col, where("key", "==", key), limit(1)));
  if (!saved.empty) return { id: saved.docs[0]!.id, name: saved.docs[0]!.get("name") as string };
  const lot: Lot = {
    name: lotName(label),
    key,
    address: extra.address?.trim() ?? "",
    projectItemId: extra.projectItemId ?? null,
    projectItemName: extra.projectItemName ?? "",
    homeownerName: "",
    homeownerPhone: "",
    homeownerEmail: "",
    note: "",
    status: "open",
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
  const ref = await addDoc(col, lot);
  return { id: ref.id, name: lot.name };
}
